import React, {useState, useEffect} from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import { ProtectedFinancialCard } from '../../components/ProtectedSensitiveData';
import * as XLSX from 'xlsx';
import CommerciauxDashboard from './CommerciauxDashboard';
import './CommerciauxManagement.css';

const CommerciauxManagement = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [stats, setStats] = useState({
    totalCommerciaux: 0,
    totalVentes: 0,
    totalClients: 0,
    totalKWc: 0,
    totalCommissions: 0,
    clientsSansCommission: 0
  });

  // États pour les modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [activeView, setActiveView] = useState('liste');

  // Recherche, filtres et tri
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('tous');
  const [filterSansComm, setFilterSansComm] = useState('tous');
  const [sortOption, setSortOption] = useState('nom_asc');

  // État pour le formulaire
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    mail: '',
    numeroTelephone: '',
    adresse: '',
    region: '',
    ville: '',
    age: '',
    totalSold: 0,
    pourcentage: 0,
    totalClients: 0
  });

  // État pour le formulaire de statistiques
  const [statsFormData, setStatsFormData] = useState({
    totalSold: 0,
    pourcentage: 0,
    totalClients: 0
  });

  const clearMessages = () => {
    setError(null);
    setMessage(null);
  };

  const API_URL = import.meta.env.VITE_API_URL;

  const getApiUrl = (endpoint) => {
    const baseUrl = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
    return `${baseUrl}${endpoint}`;
  };

  useEffect(() => {
    const userType = localStorage.getItem('typeUser');
    if (userType !== 'admin' && userType !== 'moderateur') {
      onNavigate('profile');
      return;
    }
    if (userType === 'moderateur' && !hasPermission('commerciaux', 'read')) {
      onNavigate('dashboard');
      return;
    }
    fetchCommerciaux();
    fetchStats();
  }, []);

  const fetchCommerciaux = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Token non trouvé. Veuillez vous reconnecter.');
        setLoading(false);
        return;
      }
      const response = await axios.get(getApiUrl('/commerciaux'), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setCommerciaux(Array.isArray(response.data) ? response.data : (response.data?.data || []));
      setError(null);
    } catch (err) {
      console.error('Erreur fetchCommerciaux:', err);
      setError('Erreur lors du chargement des commerciaux');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/commerciaux/stats'), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setStats(response.data);
    } catch (err) {
      console.error('Erreur fetchStats:', err);
    }
  };

  const handleEdit = (commercial) => {
    setSelectedCommercial(commercial);
    setFormData({
      nom: commercial.nom,
      prenom: commercial.prenom,
      mail: commercial.mail,
      numeroTelephone: commercial.numeroTelephone || '',
      adresse: commercial.adresse || '',
      region: commercial.region || '',
      ville: commercial.ville || '',
      age: commercial.age || '',
      totalSold: commercial.totalSold || 0,
      pourcentage: commercial.pourcentage || 0,
      totalClients: commercial.totalClients || 0
    });
    setShowEditModal(true);
  };

  const handleDelete = (commercial) => {
    setSelectedCommercial(commercial);
    setShowDeleteModal(true);
  };

  const handleDetail = (commercial) => {
    onNavigate('commercial-detail', commercial._id);
  };

  const handleUpdateStats = (commercial) => {
    setSelectedCommercial(commercial);
    setStatsFormData({
      totalSold: commercial.totalSold || 0,
      pourcentage: commercial.pourcentage || 0,
      totalClients: commercial.totalClients || 0
    });
    setShowStatsModal(true);
  };

  const handleAdd = () => {
    setFormData({
      nom: '',
      prenom: '',
      mail: '',
      numeroTelephone: '',
      adresse: '',
      region: '',
      ville: '',
      age: '',
      totalSold: 0,
      pourcentage: 0,
      totalClients: 0
    });
    setShowAddModal(true);
  };

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  const handleStatsInputChange = (e) => {
    const {name, value} = e.target;
    setStatsFormData(prev => ({...prev, [name]: parseFloat(value) || 0}));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl('/commerciaux'), formData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowAddModal(false);
      fetchCommerciaux();
      fetchStats();
      setMessage('Commercial ajouté avec succès');
    } catch (err) {
      setError(err.response?.status === 409 ? 'Cet email est déjà utilisé.' : 'Erreur lors de l\'ajout');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.put(getApiUrl(`/commerciaux/${selectedCommercial._id}`), formData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowEditModal(false);
      fetchCommerciaux();
      fetchStats();
      setMessage('Commercial modifié avec succès');
    } catch (err) {
      setError('Erreur lors de la modification');
    }
  };

  const handleStatsSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.put(getApiUrl(`/commerciaux/${selectedCommercial._id}/stats`), statsFormData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowStatsModal(false);
      fetchCommerciaux();
      fetchStats();
      setMessage('Statistiques mises à jour');
    } catch (err) {
      setError('Erreur lors de la mise à jour des stats');
    }
  };

  const handleDeleteConfirm = async () => {
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(getApiUrl(`/commerciaux/${selectedCommercial._id}`), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowDeleteModal(false);
      fetchCommerciaux();
      fetchStats();
      setMessage('Commercial supprimé');
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowAddModal(false);
    setShowStatsModal(false);
    setSelectedCommercial(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  };

  const formatNumber2 = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  };

  const formatKWc = (value) => {
    return Math.round((Number(value) || 0) * 10) / 10;
  };

  const availableRegions = React.useMemo(() => {
    const set = new Set();
    commerciaux.forEach(c => {
      if (c.region?.trim()) set.add(c.region.trim());
    });
    return Array.from(set).sort();
  }, [commerciaux]);

  const filteredCommerciaux = React.useMemo(() => {
    return commerciaux
      .filter(com => {
        const perf = com.performance || {};
        const term = searchTerm.trim().toLowerCase();
        if (term) {
          const fullName = `${com.prenom || ''} ${com.nom || ''}`.toLowerCase();
          const email = (com.mail || '').toLowerCase();
          const tel = (com.numeroTelephone || '').toLowerCase();
          if (!fullName.includes(term) && !email.includes(term) && !tel.includes(term)) return false;
        }

        if (filterRegion !== 'tous' && com.region !== filterRegion) return false;
        if (filterSansComm === 'oui' && !(perf.clientsSansCommission > 0)) return false;
        if (filterSansComm === 'non' && perf.clientsSansCommission > 0) return false;

        return true;
      })
      .sort((a, b) => {
        const perfA = a.performance || {};
        const perfB = b.performance || {};

        switch (sortOption) {
          case 'nom_asc':
            return `${a.prenom || ''} ${a.nom || ''}`.localeCompare(`${b.prenom || ''} ${b.nom || ''}`);
          case 'nom_desc':
            return `${b.prenom || ''} ${b.nom || ''}`.localeCompare(`${a.prenom || ''} ${a.nom || ''}`);
          case 'ventes_desc':
            return (perfB.totalVentes || 0) - (perfA.totalVentes || 0);
          case 'ventes_asc':
            return (perfA.totalVentes || 0) - (perfB.totalVentes || 0);
          case 'kwc_desc':
            return (perfB.totalKWc || 0) - (perfA.totalKWc || 0);
          case 'kwc_asc':
            return (perfA.totalKWc || 0) - (perfB.totalKWc || 0);
          case 'commission_desc':
            return (perfB.commissionGagnee || 0) - (perfA.commissionGagnee || 0);
          case 'clients_desc':
            return (perfB.totalClients || 0) - (perfA.totalClients || 0);
          default:
            return 0;
        }
      });
  }, [commerciaux, searchTerm, filterRegion, filterSansComm, sortOption]);

  const handleExportExcel = () => {
    const exportData = commerciaux.map(com => ({
      'ID COMMERCIAL': com._id || '',
      'NOM': com.nom || '',
      'PRÉNOM': com.prenom || '',
      'EMAIL': com.mail || '',
      'TÉLÉPHONE': com.numeroTelephone || '',
      'ADRESSE': com.adresse || '',
      'RÉGION': com.region || '',
      'VILLE': com.ville || '',
      'ÂGE': com.age || '',
      'TOTAL VENTES (TND)': com.performance?.totalVentes || com.totalSold || 0,
      'COMMISSION GAGNÉE (TND)': com.performance?.commissionGagnee || 0,
      'TOTAL KWC': com.performance?.totalKWc || 0,
      'TOTAL DOSSIERS': com.performance?.totalClients || com.totalClients || 0,
      'DOSSIERS SANS COMM.': com.performance?.clientsSansCommission || 0,
      'POURCENTAGE BASE (%)': com.pourcentage || 0,
      'DATE DE CRÉATION': com.createdAt ? new Date(com.createdAt).toLocaleDateString('fr-FR') : ''
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Commerciaux');
    XLSX.writeFile(workbook, 'Commerciaux.xlsx');
  };

  return (
    <div className="cm-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

      <div className="cm-content">
        <div className="cm-header">
          <div className="cm-header-text">
            <h1>Gestion des Commerciaux</h1>
            <p>Administration de l'équipe commerciale</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="cm-btn-add" style={{backgroundColor: '#10b981'}} onClick={handleExportExcel}>
              <i className="fas fa-file-excel"></i> Exporter
            </button>
            {hasPermission('commerciaux', 'create') && (
              <button className="cm-btn-add" onClick={handleAdd}>
                <i className="fas fa-plus"></i> Ajouter un commercial
              </button>
            )}
          </div>
        </div>

        <div className="cm-view-switch">
          <button className={activeView === 'liste' ? 'active' : ''} onClick={() => setActiveView('liste')}>
            <i className="fas fa-list"></i> Liste commerciaux
          </button>
          <button className={activeView === 'dashboard' ? 'active' : ''} onClick={() => setActiveView('dashboard')}>
            <i className="fas fa-chart-pie"></i> Dashboard commerciaux
          </button>
        </div>

        {activeView === 'dashboard' ? (
          <CommerciauxDashboard
            getApiUrl={getApiUrl}
            commerciaux={commerciaux}
            onNavigate={onNavigate}
          />
        ) : (
        <>
        {/* Statistiques */}
        <div className="cm-stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
          <div className="cm-stat-card cm-stat-count">
            <div className="cm-stat-body">
              <div className="cm-stat-top">
                <span className="cm-stat-icon">👥</span>
                <span className="cm-stat-label">Commerciaux</span>
              </div>
              <div className="cm-stat-number">{stats.totalCommerciaux}</div>
            </div>
          </div>
          <ProtectedFinancialCard title="Total Ventes protégé" lockedClassName="cm-stat-card cm-stat-sales">
          <div className="cm-stat-card cm-stat-sales">
            <div className="cm-stat-body">
              <div className="cm-stat-top">
                <span className="cm-stat-icon">💰</span>
                <span className="cm-stat-label">Total Ventes</span>
              </div>
              <div className="cm-stat-number">{formatCurrency(stats.totalVentes || 0)}</div>
            </div>
          </div>
          </ProtectedFinancialCard>
          <div className="cm-stat-card cm-stat-avg">
            <div className="cm-stat-body">
              <div className="cm-stat-top">
                <span className="cm-stat-icon">⚡</span>
                <span className="cm-stat-label">Total kWc</span>
              </div>
              <div className="cm-stat-number">{formatKWc(stats.totalKWc)} kWc</div>
            </div>
          </div>
          <div className="cm-stat-card cm-stat-clients">
            <div className="cm-stat-body">
              <div className="cm-stat-top">
                <span className="cm-stat-icon">🤝</span>
                <span className="cm-stat-label">Total Dossiers</span>
              </div>
              <div className="cm-stat-number">{stats.totalClients || 0}</div>
            </div>
          </div>
          <div className="cm-stat-card" style={{ borderLeft: '4px solid #e53e3e' }}>
            <div className="cm-stat-body">
              <div className="cm-stat-top">
                <span className="cm-stat-icon">⚠️</span>
                <span className="cm-stat-label">Sans Commission</span>
              </div>
              <div className="cm-stat-number" style={{ color: '#e53e3e' }}>{stats.clientsSansCommission || 0}</div>
            </div>
          </div>
        </div>

        {error && <div className="cm-message cm-message-error">{error}</div>}
        {message && <div className="cm-message cm-message-success">{message}</div>}

        {/* Toolbar Recherche, Filtres & Tri */}
        <div className="cm-toolbar">
          <div className="cm-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="cm-search-clear" onClick={() => setSearchTerm('')}>&times;</button>
            )}
          </div>

          <div className="cm-filter-group">
            {availableRegions.length > 0 && (
              <div className="cm-filter-item">
                <label><i className="fas fa-map-marker-alt"></i> Région</label>
                <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}>
                  <option value="tous">Toutes les régions</option>
                  {availableRegions.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="cm-filter-item">
              <label><i className="fas fa-exclamation-triangle"></i> Sans commission</label>
              <select value={filterSansComm} onChange={e => setFilterSansComm(e.target.value)}>
                <option value="tous">Tous les commerciaux</option>
                <option value="oui">À traiter uniquement</option>
                <option value="non">Aucun dossier sans commission</option>
              </select>
            </div>

            <div className="cm-filter-item">
              <label><i className="fas fa-sort"></i> Trier par</label>
              <select value={sortOption} onChange={e => setSortOption(e.target.value)}>
                <option value="nom_asc">Nom & Prénom (A → Z)</option>
                <option value="nom_desc">Nom & Prénom (Z → A)</option>
                <option value="ventes_desc">Total Ventes (↓ Décroissant)</option>
                <option value="ventes_asc">Total Ventes (↑ Croissant)</option>
                <option value="kwc_desc">Total kWc (↓ Décroissant)</option>
                <option value="kwc_asc">Total kWc (↑ Croissant)</option>
                <option value="commission_desc">Commission (↓ Décroissant)</option>
                <option value="clients_desc">Nb Clients (↓ Décroissant)</option>
              </select>
            </div>

            {(searchTerm || filterRegion !== 'tous' || filterSansComm !== 'tous' || sortOption !== 'nom_asc') && (
              <button
                className="cm-reset-filters-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilterRegion('tous');
                  setFilterSansComm('tous');
                  setSortOption('nom_asc');
                }}
                title="Réinitialiser les filtres"
              >
                <i className="fas fa-undo"></i> Réinitialiser
              </button>
            )}
          </div>
        </div>

        <div className="cm-table-container">
          {loading ? (
            <LoadingSpinner message="Chargement des commerciaux..." />
          ) : (
            <table className="cm-table">
              <thead>
                <tr>
                  <th>Nom & Prénom</th>
                  <th>Total Ventes</th>
                  <th>Total kWc</th>
                  <th>Commissions</th>
                  <th>Nb Clients</th>
                  <th>Sans Comm.</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCommerciaux.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                      <i className="fas fa-search" style={{ fontSize: '2rem', display: 'block', marginBottom: '8px', opacity: 0.4 }}></i>
                      Aucun commercial ne correspond à vos critères de recherche.
                    </td>
                  </tr>
                ) : (
                  filteredCommerciaux.map((com) => {
                    const perf = com.performance || {};
                    return (
                    <tr key={com._id}>
                      <td>
                        <div className="cm-commercial-info">
                          <div className="cm-avatar">{com.prenom[0]}{com.nom[0]}</div>
                          <div className="cm-com-details">
                            <strong>{com.prenom} {com.nom}</strong>
                            <small>{com.mail}</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="cm-total-sold">{formatCurrency(perf.totalVentes || 0)}</span></td>
                      <td><strong>{formatKWc(perf.totalKWc)} kWc</strong></td>
                      <td><span className="cm-badge cm-badge-commission" style={{background: '#d1fae5', color: '#065f46'}}>{formatCurrency(perf.commissionGagnee || 0)}</span></td>
                      <td><span className="cm-badge cm-badge-clients">{perf.totalClients || 0}</span></td>
                      <td>
                        {perf.clientsSansCommission > 0 ? (
                          <span className="cm-badge" style={{background: '#fee2e2', color: '#991b1b'}}>{perf.clientsSansCommission} à traiter</span>
                        ) : (
                          <span className="cm-badge" style={{background: '#f3f4f6', color: '#6b7280'}}>0</span>
                        )}
                      </td>
                      <td>
                        <div className="cm-actions">
                          <button className="cm-btn-action cm-btn-view" onClick={() => handleDetail(com)} title="Détails">
                            <i className="fas fa-eye"></i>
                          </button>
                          {hasPermission('commerciaux', 'update') && (
                            <button className="cm-btn-action cm-btn-edit" onClick={() => handleEdit(com)} title="Modifier">
                              <i className="fas fa-edit"></i>
                            </button>
                          )}
                          {hasPermission('commerciaux', 'delete') && (
                            <button className="cm-btn-action cm-btn-delete" onClick={() => handleDelete(com)} title="Supprimer">
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}

        {/* Modal d'ajout */}
        {showAddModal && (
          <div className="cm-modal-overlay">
            <div className="cm-modal-content" onClick={e => e.stopPropagation()}>
              <div className="cm-modal-header">
                <h2>Ajouter un commercial</h2>
                <button className="cm-modal-close" onClick={closeModals}>×</button>
              </div>
              <form onSubmit={handleAddSubmit}>
                <div className="cm-modal-body">
                  <div className="cm-form-row">
                    <div className="cm-form-group"><label>Prénom *</label><input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} required /></div>
                    <div className="cm-form-group"><label>Nom *</label><input type="text" name="nom" value={formData.nom} onChange={handleInputChange} required /></div>
                  </div>
                  <div className="cm-form-row">
                    <div className="cm-form-group"><label>Email *</label><input type="email" name="mail" value={formData.mail} onChange={handleInputChange} required /></div>
                    <div className="cm-form-group"><label>Téléphone</label><input type="tel" name="numeroTelephone" value={formData.numeroTelephone} onChange={handleInputChange} /></div>
                  </div>
                  <div className="cm-form-row">
                    <div className="cm-form-group"><label>Âge</label><input type="number" name="age" value={formData.age} onChange={handleInputChange} /></div>
                    <div className="cm-form-group"><label>Ville</label><input type="text" name="ville" value={formData.ville} onChange={handleInputChange} /></div>
                  </div>
                  <div className="cm-form-group"><label>Adresse</label><textarea name="adresse" value={formData.adresse} onChange={handleInputChange} rows="2" /></div>
                </div>
                <div className="cm-modal-footer">
                  <button type="button" className="cm-btn cm-btn-cancel" onClick={closeModals}>Annuler</button>
                  <button type="submit" className="cm-btn cm-btn-save">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal d'édition */}
        {showEditModal && (
          <div className="cm-modal-overlay">
            <div className="cm-modal-content" onClick={e => e.stopPropagation()}>
              <div className="cm-modal-header">
                <h2>Modifier le commercial</h2>
                <button className="cm-modal-close" onClick={closeModals}>×</button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="cm-modal-body">
                  <div className="cm-form-row">
                    <div className="cm-form-group"><label>Prénom *</label><input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} required /></div>
                    <div className="cm-form-group"><label>Nom *</label><input type="text" name="nom" value={formData.nom} onChange={handleInputChange} required /></div>
                  </div>
                  <div className="cm-form-row">
                    <div className="cm-form-group"><label>Email *</label><input type="email" name="mail" value={formData.mail} onChange={handleInputChange} required /></div>
                    <div className="cm-form-group"><label>Téléphone</label><input type="tel" name="numeroTelephone" value={formData.numeroTelephone} onChange={handleInputChange} /></div>
                  </div>
                  <div className="cm-modal-footer">
                    <button type="button" className="cm-btn cm-btn-cancel" onClick={closeModals}>Annuler</button>
                    <button type="submit" className="cm-btn cm-btn-save">Enregistrer</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Stats */}
        {showStatsModal && (
          <div className="cm-modal-overlay">
            <div className="cm-modal-content" onClick={e => e.stopPropagation()}>
              <div className="cm-modal-header">
                <h2>Statistiques : {selectedCommercial.prenom}</h2>
                <button className="cm-modal-close" onClick={closeModals}>×</button>
              </div>
              <form onSubmit={handleStatsSubmit}>
                <div className="cm-modal-body">
                  <div className="cm-form-group"><label>Total Ventes (TND)</label><input type="number" name="totalSold" value={statsFormData.totalSold} onChange={handleStatsInputChange} step="0.01" required /></div>
                  <div className="cm-form-group"><label>Pourcentage Commission (%)</label><input type="number" name="pourcentage" value={statsFormData.pourcentage} onChange={handleStatsInputChange} step="0.01" required /></div>
                  <div className="cm-form-group"><label>Nb Clients</label><input type="number" name="totalClients" value={statsFormData.totalClients} onChange={handleStatsInputChange} required /></div>
                </div>
                <div className="cm-modal-footer">
                  <button type="button" className="cm-btn cm-btn-cancel" onClick={closeModals}>Annuler</button>
                  <button type="submit" className="cm-btn cm-btn-save">Mettre à jour</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Suppression */}
        {showDeleteModal && (
          <div className="cm-modal-overlay">
            <div className="cm-modal-content" onClick={e => e.stopPropagation()}>
              <div className="cm-modal-header"><h2>Confirmation</h2><button className="cm-modal-close" onClick={closeModals}>×</button></div>
              <div className="cm-modal-body">
                <p>Supprimer <strong>{selectedCommercial.prenom} {selectedCommercial.nom}</strong> ?</p>
                <p style={{color: '#e53e3e', marginTop: '10px'}}>Cette action est irréversible.</p>
              </div>
              <div className="cm-modal-footer">
                <button type="button" className="cm-btn cm-btn-cancel" onClick={closeModals}>Annuler</button>
                <button type="button" className="cm-btn" style={{backgroundColor: '#e53e3e', color: 'white'}} onClick={handleDeleteConfirm}>Supprimer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommerciauxManagement;
